import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { GeocodingProvider } from '../../../core/models/org-settings.model';
import { EnvironmentService } from '../../../core/services/environment.service';
import { MapDisplaySettings, MapDisplaySettingsService } from '../../../core/services/map-display-settings.service';
import { OrgSettingsService } from '../../../core/services/org-settings.service';
import { PermissionService } from '../../../core/services/permission.service';
import { ToastService } from '../../../core/services/toast.service';
import { TranslationService } from '../../../core/services/translation.service';
import { UserStorageService } from '../../../core/services/user-storage.service';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { CheckboxComponent } from '../../../shared/components/form-controls/checkbox/checkbox.component';
import { InputComponent } from '../../../shared/components/form-controls/input/input.component';
import { SelectComponent } from '../../../shared/components/form-controls/select/select.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

/** Grab-bag of settings that don't warrant their own routed section --
 *  leaderboard, camera behavior, demo-mode artwork override, and feature
 *  flags -- moved out of the old monolithic settings page. */
@Component({
  selector: 'app-general-settings',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    DatePipe,
    BackButtonComponent,
    CardComponent,
    CardContentComponent,
    CheckboxComponent,
    InputComponent,
    SelectComponent,
    TranslatePipe,
  ],
  templateUrl: './general-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./general-settings.component.css', '../../../../css/settings-shared.css'],
})
export class GeneralSettingsComponent implements OnInit {
  private orgSettingsService = inject(OrgSettingsService);
  private toast = inject(ToastService);
  private translate = inject(TranslationService);
  private permissionService = inject(PermissionService);
  readonly environmentService = inject(EnvironmentService);
  private userStorage = inject(UserStorageService);
  private mapDisplaySettings = inject(MapDisplaySettingsService);

  leaderboardEnabledValue = true;

  geocodingProviderValue: GeocodingProvider = 'here';
  readonly geocodingProviderOptions: { value: GeocodingProvider; label: string }[] = [
    { value: 'radar', label: 'settings.geocoding.provider.radar' },
    { value: 'here', label: 'settings.geocoding.provider.here' },
  ];

  // Camera timeout setting (in seconds)
  cameraTimeout = signal(10); // Default 10 seconds
  cameraTimeoutValue = 10; // For two-way binding with ngModel
  private readonly CAMERA_TIMEOUT_STORAGE_KEY = 'camera-auto-stop-timeout';

  // Demo mode properties
  canOverrideArtwork = false;
  currentEnvironment = '';
  artworkEnvironmentOptions: { value: string; label: string }[] = [];
  selectedArtworkEnv = '';
  expiryMinutes = 15;
  overrideExpiresAt: Date | null = null;

  // Map coverage layer display settings (see MapDisplaySettingsService, read by
  // map.component.ts's addRegistryLayers/reloadOwnActivity) -- storage is per-user
  // (localStorage, not org data), but editing is still gated to org-owner/admin via
  // 'config.manage-map-display' (see backend/schemas/migrations/20260920000001_config_
  // manage_map_display_permission.sql), same as canOverrideArtwork below gates a
  // similarly local, admin-only tweak on 'feature.artwork-override'.
  canManageMapDisplay = false;
  mapDisplayForm = new FormGroup({});
  mapDisplayModel: MapDisplaySettings = {
    activeRegionColor: this.mapDisplaySettings.activeRegionColor(),
    activeRegionOpacity: this.mapDisplaySettings.activeRegionOpacity(),
    inactiveRegionOpacity: this.mapDisplaySettings.inactiveRegionOpacity(),
  };
  mapDisplayFields: FormlyFieldConfig[] = [];

  async ngOnInit() {
    this.leaderboardEnabledValue = await this.orgSettingsService.isLeaderboardEnabled();
    this.geocodingProviderValue = await this.orgSettingsService.getGeocodingProvider();

    this.canManageMapDisplay = this.permissionService.has('config.manage-map-display');

    const t = (key: string) => this.translate.translate(key);
    this.mapDisplayFields = [
      {
        key: 'activeRegionColor',
        type: 'color',
        props: {
          label: t('settings.mapDisplay.activeRegionColor.label'),
          description: t('settings.mapDisplay.activeRegionColor.description'),
        },
      },
      {
        key: 'activeRegionOpacity',
        type: 'number',
        props: {
          label: t('settings.mapDisplay.activeRegionOpacity.label'),
          description: t('settings.mapDisplay.activeRegionOpacity.description'),
          min: 0,
          max: 1,
          step: 0.05,
        },
      },
      {
        key: 'inactiveRegionOpacity',
        type: 'number',
        props: {
          label: t('settings.mapDisplay.inactiveRegionOpacity.label'),
          description: t('settings.mapDisplay.inactiveRegionOpacity.description'),
          min: 0,
          max: 1,
          step: 0.05,
        },
      },
    ];

    const storedTimeout = this.userStorage.getItem(this.CAMERA_TIMEOUT_STORAGE_KEY);
    if (storedTimeout) {
      const timeoutValue = parseInt(storedTimeout, 10);
      if (!isNaN(timeoutValue) && timeoutValue >= 5 && timeoutValue <= 180) {
        this.cameraTimeout.set(timeoutValue);
        this.cameraTimeoutValue = timeoutValue;
      }
    }

    this.initializeDemoMode();
  }

  async toggleLeaderboardEnabled() {
    await this.orgSettingsService.setLeaderboardEnabled(this.leaderboardEnabledValue);
  }

  async onGeocodingProviderChange() {
    await this.orgSettingsService.setGeocodingProvider(this.geocodingProviderValue);
  }

  onCameraTimeoutChange(value: number) {
    if (!isNaN(value) && value >= 5 && value <= 180) {
      this.cameraTimeout.set(value);
      this.cameraTimeoutValue = value;
      this.userStorage.setItem(this.CAMERA_TIMEOUT_STORAGE_KEY, value.toString());
    } else {
      this.toast.error(this.translate.translate('settings.camera.cameraTimeout.invalidRange'));
    }
  }

  onMapDisplayModelChange(model: MapDisplaySettings): void {
    this.mapDisplaySettings.setActiveRegionColor(model.activeRegionColor);
    this.mapDisplaySettings.setActiveRegionOpacity(model.activeRegionOpacity);
    this.mapDisplaySettings.setInactiveRegionOpacity(model.inactiveRegionOpacity);
  }

  private initializeDemoMode(): void {
    this.canOverrideArtwork = this.permissionService.has('feature.artwork-override');

    if (!this.canOverrideArtwork) return;

    this.currentEnvironment = this.environmentService.getCurrentEnvironment();
    this.artworkEnvironmentOptions = this.environmentService.getAvailableEnvironments();

    const override = this.environmentService.getCurrentOverride();
    if (override) {
      this.selectedArtworkEnv = override.environment;
      this.overrideExpiresAt = override.expiresAt;
    } else {
      this.selectedArtworkEnv = this.currentEnvironment;
    }

    this.expiryMinutes = this.environmentService.getDefaultExpiryMinutes();
  }

  onArtworkEnvChange(environmentName: string): void {
    this.environmentService.setArtworkOverride(environmentName, this.expiryMinutes);

    const override = this.environmentService.getCurrentOverride();
    if (override) {
      this.overrideExpiresAt = override.expiresAt;
    } else {
      this.overrideExpiresAt = null;
    }
  }
}
